import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { http, fileUrl, formatApiError, STATUS_LABELS, API } from "@/lib/api";
import PortalHeader from "@/components/PortalHeader";
import StatusBadge from "@/components/StatusBadge";
import SmsTapToSend from "@/components/SmsTapToSend";
import { enableAdminPush, disableAdminPush, getSubscription, isStandalone, isIOS, pushSupported, sendAdminPushTest } from "@/lib/push";
import { toast } from "sonner";
import {
  Loader2, Bell, Settings as SettingsIcon, Users, Search,
  X, CheckCircle2, XCircle, CalendarClock, Send, Plus, Mail,
  Calendar as CalendarIcon, Upload, Inbox, Image as ImageIcon, Eye, EyeOff, Trash2, MoveUp, MoveDown,
  BellRing, BellOff, MessageSquare, AlertTriangle, ChevronRight
} from "lucide-react";

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "lead", label: "Lead" },
  { key: "consult", label: "Consult" },
  { key: "deposit_pending", label: "Deposit Pending" },
  { key: "deposit_paid", label: "Deposit Paid" },
  { key: "booked", label: "Booked" },
];

export default function AdminDashboard() {
  // Default tab = Reminders (the daily workflow), but restore last-opened tab.
  // Also honors ?appt=<id> deep link from a tapped push notification.
  const [tab, setTab] = useState(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("appt")) return "reminders";
      const saved = localStorage.getItem("paiddewz_admin_tab");
      if (saved) return saved;
    }
    return "reminders";
  });
  // Persist tab so it sticks across reloads on the home-screen PWA.
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("paiddewz_admin_tab", tab);
    }
  }, [tab]);

  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });

  const loadClients = useCallback(async () => {
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await http.get("/admin/clients", { params });
      setClients(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }, [filter]);

  const loadStats = useCallback(async () => {
    try { const { data } = await http.get("/admin/stats"); setStats(data); } catch (e) {}
  }, []);

  const loadNotifs = useCallback(async () => {
    try { const { data } = await http.get("/admin/notifications"); setNotifs(data); } catch (e) {}
  }, []);

  useEffect(() => {
    Promise.all([loadClients(), loadStats(), loadNotifs()]).finally(() => setLoading(false));
  }, [loadClients, loadStats, loadNotifs]);

  // Poll notifications every 30s
  useEffect(() => {
    const t = setInterval(() => { loadNotifs(); loadStats(); }, 30000);
    return () => clearInterval(t);
  }, [loadNotifs, loadStats]);

  const filtered = clients.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s) || c.phone?.includes(s));
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A120E]">
        <Loader2 className="w-8 h-8 animate-spin text-[#D2A853]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A120E] admin-mobile-shell">
      <PortalHeader
        title="Admin Console"
        right={
          <div className="flex items-center gap-4">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#A3A8A4] hover:text-[#D2A853] transition flex items-center gap-2"
              data-testid="admin-preview-landing-link"
              title="Open the public landing page in a new tab"
            >
              <Eye className="w-5 h-5" strokeWidth={1.4} />
              <span className="hidden md:inline overline">Preview Public Landing</span>
            </a>
            <Link to="/admin/calendar" className="text-[#A3A8A4] hover:text-[#D2A853] transition flex items-center gap-2" data-testid="admin-calendar-link">
              <CalendarIcon className="w-5 h-5" strokeWidth={1.4} />
              <span className="hidden md:inline overline">Calendar</span>
            </Link>
            <button
              onClick={() => setTab("notifications")}
              className="relative text-[#A3A8A4] hover:text-[#D2A853] transition"
              data-testid="notifications-bell"
            >
              <Bell className="w-5 h-5" strokeWidth={1.4} />
              {notifs.unread > 0 && (
                <span className="absolute -top-1 -right-2 bg-[#D2A853] text-[#0A120E] text-[10px] font-bold px-1.5 rounded-full">
                  {notifs.unread}
                </span>
              )}
            </button>
          </div>
        }
      />
      <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-12 py-6 md:py-10">
        {/* Mobile-first: Needs Attention up top */}
        <NeedsAttentionPanel
          onOpenClient={(id) => { setSelected({ id }); }}
          onOpenAppt={(id) => { setTab("reminders"); /* Reminders panel renders the appt list */ }}
        />

        {/* Stats — 2 cols on mobile (bigger numbers), 6 on desktop */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8 md:mb-10" data-testid="stats-grid">
            <StatCard label="Total" value={stats.total_clients} />
            {Object.keys(STATUS_LABELS).slice(0, 5).map((s) => (
              <StatCard key={s} label={STATUS_LABELS[s]} value={stats.counts?.[s] || 0} accent={s === "deposit_paid" || s === "booked"} />
            ))}
          </div>
        )}

        {/* Top Tabs — desktop only. Mobile uses fixed bottom nav. */}
        <div className="hidden md:flex gap-1 mb-6 border-b border-[#D2A853]/15 overflow-x-auto">
          <TabBtn active={tab === "clients"} onClick={() => setTab("clients")} testId="admin-tab-clients">
            <Users className="w-3 h-3 inline mr-2" /> Clients
          </TabBtn>
          <TabBtn active={tab === "requests"} onClick={() => setTab("requests")} testId="admin-tab-requests">
            <Inbox className="w-3 h-3 inline mr-2" /> Booking Requests
          </TabBtn>
          <TabBtn active={tab === "notifications"} onClick={() => setTab("notifications")} testId="admin-tab-notifications">
            <Bell className="w-3 h-3 inline mr-2" /> Notifications {notifs.unread > 0 && `(${notifs.unread})`}
          </TabBtn>
          <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} testId="admin-tab-settings">
            <SettingsIcon className="w-3 h-3 inline mr-2" /> Studio Settings
          </TabBtn>
          <TabBtn active={tab === "featured"} onClick={() => setTab("featured")} testId="admin-tab-featured">
            <ImageIcon className="w-3 h-3 inline mr-2" /> Featured Work
          </TabBtn>
          <TabBtn active={tab === "reminders"} onClick={() => setTab("reminders")} testId="admin-tab-reminders">
            <BellRing className="w-3 h-3 inline mr-2" /> Reminders
          </TabBtn>
        </div>

        {/* Mobile: section heading shows the active tab name so the screen has context */}
        <h2 className="md:hidden font-display text-2xl text-[#E8E9E3] mb-4" data-testid="mobile-tab-title">
          {tab === "clients" && "Clients"}
          {tab === "requests" && "Booking Requests"}
          {tab === "notifications" && "Notifications"}
          {tab === "settings" && "Studio Settings"}
          {tab === "featured" && "Featured Work"}
          {tab === "reminders" && "Reminders"}
        </h2>

        {tab === "clients" && (
          <div>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="flex gap-1 flex-wrap">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    data-testid={`filter-${f.key}`}
                    className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition ${
                      filter === f.key
                        ? "border-[#D2A853] bg-[#D2A853]/10 text-[#D2A853]"
                        : "border-[#D2A853]/20 text-[#A3A8A4] hover:border-[#D2A853]/50 hover:text-[#E8E9E3]"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="ml-auto relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A8A4]" strokeWidth={1.4} />
                <input
                  className="input-line pl-9 pr-2 min-w-[260px]"
                  placeholder="Search name, email, phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="client-search"
                />
              </div>
            </div>

            <ClientsTable clients={filtered} onSelect={setSelected} />
          </div>
        )}

        {tab === "notifications" && (
          <NotificationsPanel notifs={notifs} reload={loadNotifs} onOpenClient={(id) => {
            const c = clients.find((x) => x.id === id);
            if (c) { setSelected(c); setTab("clients"); }
          }} />
        )}

        {tab === "requests" && (
          <BookingRequestsPanel onOpenClient={(id) => {
            const c = clients.find((x) => x.id === id);
            if (c) { setSelected(c); setTab("clients"); }
          }} reloadAll={() => { loadClients(); loadStats(); loadNotifs(); }} />
        )}

        {tab === "settings" && <SettingsPanel />}
        {tab === "featured" && <FeaturedWorkPanel />}
        {tab === "reminders" && <RemindersDuePanel />}

        {/* Client Drawer */}
        {selected && (
          <ClientDrawer
            clientId={selected.id}
            onClose={() => setSelected(null)}
            onUpdate={() => { loadClients(); loadStats(); loadNotifs(); }}
          />
        )}
      </div>

      {/* Mobile bottom navigation — fixed, always visible on phones */}
      <BottomNav tab={tab} setTab={setTab} unreadCount={notifs.unread} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bottom navigation — mobile only (≤768px). Big thumb targets, icon + label.
// ---------------------------------------------------------------------------
function BottomNav({ tab, setTab, unreadCount }) {
  const items = [
    { key: "reminders",     icon: BellRing,      label: "Reminders" },
    { key: "clients",       icon: Users,         label: "Clients" },
    { key: "requests",      icon: Inbox,         label: "Requests" },
    { key: "notifications", icon: Bell,          label: "Inbox", badge: unreadCount },
    { key: "settings",      icon: SettingsIcon,  label: "Settings" },
  ];
  return (
    <nav className="bottom-nav" data-testid="admin-bottom-nav" aria-label="Admin navigation">
      {items.map((it) => {
        const Icon = it.icon;
        const active = tab === it.key;
        return (
          <button
            key={it.key}
            onClick={() => setTab(it.key)}
            data-active={active}
            data-testid={`bottom-nav-${it.key}`}
            className="bottom-nav-btn"
            aria-label={it.label}
            aria-current={active ? "page" : undefined}
          >
            <Icon className="w-5 h-5" strokeWidth={1.6} />
            <span>{it.label}</span>
            {it.badge > 0 && <span className="bottom-nav-btn-badge" data-testid={`bottom-nav-${it.key}-badge`}>{it.badge}</span>}
          </button>
        );
      })}
    </nav>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-3 overline whitespace-nowrap transition-colors ${
        active ? "text-[#D2A853] border-b-2 border-[#D2A853]" : "text-[#A3A8A4] hover:text-[#E8E9E3]"
      }`}
    >
      {children}
    </button>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`frame-card p-4 ${accent ? "border-[#4CAF50]/40" : ""}`}>
      <span className="overline text-[#A3A8A4] text-[10px] stat-label">{label}</span>
      <div className={`font-display text-3xl mt-2 stat-value ${accent ? "text-[#4CAF50]" : "text-[#E8E9E3]"}`}>{value}</div>
    </div>
  );
}

function ClientStatusChips({ c }) {
  const ns = (c.tattoo_no_show_count || 0) + (c.consultation_no_show_count || 0);
  const cn = (c.tattoo_cancel_count || 0) + (c.consultation_cancel_count || 0);
  const auto = ns >= 2 || cn >= 3;
  const flagged = c.high_risk_override === true || (c.high_risk_override !== false && auto);
  if (!flagged && ns === 0 && cn === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`client-tags-${c.id}`}>
      {flagged && (
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#CF6679]/50 text-[#CF6679]">
          High Risk
        </span>
      )}
      {ns > 0 && (
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#D2A853]/40 text-[#D2A853]">
          {ns}× No-Show
        </span>
      )}
      {cn > 0 && (
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#D2A853]/30 text-[#A3A8A4]">
          {cn}× Cancel
        </span>
      )}
    </div>
  );
}

function ClientsTable({ clients, onSelect }) {
  if (clients.length === 0) {
    return (
      <div className="frame-card p-12 text-center" data-testid="clients-empty">
        <Users className="w-8 h-8 mx-auto text-[#A3A8A4]/40 mb-4" strokeWidth={1.2} />
        <p className="text-[#A3A8A4]">No clients match your filter.</p>
      </div>
    );
  }
  return (
    <>
      {/* Mobile: card layout — single tap target per client */}
      <div className="space-y-3 md:hidden" data-testid="clients-cards">
        {clients.map((c) => (
          <button
            key={c.id}
            onClick={() => onSelect(c)}
            className="frame-card w-full text-left p-4 active:bg-[#D2A853]/5"
            data-testid={`client-card-${c.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-display text-lg text-[#E8E9E3] truncate">{c.name}</div>
                <div className="text-sm text-[#A3A8A4] mt-1 truncate">{c.email}</div>
                {c.phone && <div className="text-sm text-[#A3A8A4] truncate">{c.phone}</div>}
                <ClientStatusChips c={c} />
              </div>
              <div className="shrink-0">
                <StatusBadge status={c.status} />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Desktop: existing table */}
      <div className="hidden md:block frame-card overflow-x-auto" data-testid="clients-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#D2A853]/20 text-left">
              <th className="overline text-[#A3A8A4] py-4 px-6">Client</th>
              <th className="overline text-[#A3A8A4] py-4 px-6">Contact</th>
              <th className="overline text-[#A3A8A4] py-4 px-6">Status</th>
              <th className="overline text-[#A3A8A4] py-4 px-6 hidden lg:table-cell">Appointment</th>
              <th className="overline text-[#A3A8A4] py-4 px-6">Joined</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr
                key={c.id}
                onClick={() => onSelect(c)}
                className="border-b border-[#D2A853]/10 hover:bg-[#D2A853]/5 cursor-pointer transition"
                data-testid={`client-row-${c.id}`}
              >
                <td className="py-4 px-6">
                  <div className="font-medium text-[#E8E9E3]">{c.name}</div>
                  <ClientStatusChips c={c} />
                </td>
                <td className="py-4 px-6 text-[#A3A8A4]">
                  <div>{c.email}</div>
                  <div className="text-xs">{c.phone}</div>
                </td>
                <td className="py-4 px-6"><StatusBadge status={c.status} /></td>
                <td className="py-4 px-6 hidden lg:table-cell text-[#A3A8A4]">
                  {c.appointment_date ? new Date(c.appointment_date).toLocaleDateString() : "—"}
                </td>
                <td className="py-4 px-6 text-[#A3A8A4] text-xs">
                  {new Date(c.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ClientDrawer({ clientId, onClose, onUpdate }) {
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("overview");
  const [noteText, setNoteText] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [apptDate, setApptDate] = useState("");
  const [apptDur, setApptDur] = useState(120);
  const [apptNotes, setApptNotes] = useState("");
  const [apptType, setApptType] = useState("tattoo");
  const [apptFormat, setApptFormat] = useState("phone");

  const load = useCallback(async () => {
    try {
      const [d, m] = await Promise.all([
        http.get(`/admin/clients/${clientId}`),
        http.get(`/admin/messages/${clientId}`),
      ]);
      setData(d.data);
      setMessages(m.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const action = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); load(); onUpdate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!data) {
    return (
      <div className="fixed inset-0 z-40 bg-[#0A120E]/80 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D2A853]" />
      </div>
    );
  }

  const u = data.user;
  const payment = data.payment;

  return (
    <div className="fixed inset-0 z-40 bg-[#0A120E]/80 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="client-drawer">
      <div
        className="w-full max-w-2xl h-full bg-[#0A120E] border-l border-[#D2A853]/30 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer header */}
        <div className="sticky top-0 z-10 backdrop-blur-xl bg-[#0A120E]/80 border-b border-[#D2A853]/20 px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="font-display text-2xl text-[#E8E9E3]">{u.name}</h2>
            <div className="flex items-center gap-3 mt-1">
              <StatusBadge status={u.status} />
              <span className="text-xs text-[#A3A8A4]">{u.email}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-[#A3A8A4] hover:text-[#D2A853]" data-testid="drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer tabs */}
        <div className="flex border-b border-[#D2A853]/15 px-6">
          <DrawerTab active={tab === "overview"} onClick={() => setTab("overview")} testId="drawer-tab-overview">Overview</DrawerTab>
          <DrawerTab active={tab === "messages"} onClick={() => setTab("messages")} testId="drawer-tab-messages">Messages ({messages.length})</DrawerTab>
          <DrawerTab active={tab === "notes"} onClick={() => setTab("notes")} testId="drawer-tab-notes">Notes ({data.notes.length})</DrawerTab>
        </div>

        <div className="p-6 space-y-6">
          {tab === "overview" && (
            <>
              {/* Contact */}
              <ContactSection user={u} reload={() => { load(); onUpdate(); }} />

              {/* Invite/Status (for imported clients) */}
              <InviteSection user={u} reload={() => { load(); onUpdate(); }} />

              {/* Project */}
              {data.intake && (
                <Section title="Project Brief">
                  <p className="text-sm text-[#E8E9E3] whitespace-pre-wrap mb-3">{data.intake.project_description}</p>
                  <Row k="Placement" v={data.intake.placement} />
                  <Row k="Size" v={data.intake.size} />
                  {data.intake.budget && <Row k="Budget" v={data.intake.budget} />}
                </Section>
              )}

              {/* References */}
              {data.files?.length > 0 && (
                <Section title={`References (${data.files.length})`}>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {data.files.map((f) => (
                      <a key={f.id} href={fileUrl(f.id)} target="_blank" rel="noopener noreferrer" className="aspect-square border border-[#D2A853]/20 hover:border-[#D2A853]/60 overflow-hidden">
                        <img src={fileUrl(f.id)} alt={f.filename} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </Section>
              )}

              {/* Deposit */}
              <Section title="Deposit">
                {payment?.status === "confirmed" ? (
                  <div className="text-[#4CAF50] text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Confirmed on {new Date(payment.confirmed_at).toLocaleDateString()}
                  </div>
                ) : payment?.status === "pending_confirmation" ? (
                  <div className="space-y-3">
                    <div className="text-[#E2BC6A] text-sm">
                      Client claims paid on {new Date(payment.client_marked_at).toLocaleString()}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => action(() => http.post(`/admin/clients/${clientId}/deposit/confirm`), "Deposit confirmed")}
                        disabled={busy}
                        className="btn-brass flex items-center gap-2 flex-1 justify-center"
                        data-testid="confirm-deposit-btn"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Mark Deposit Received
                      </button>
                      <button
                        onClick={() => action(() => http.post(`/admin/clients/${clientId}/deposit/reject`), "Deposit rejected")}
                        disabled={busy}
                        className="px-4 py-2 border border-[#CF6679]/60 text-[#CF6679] hover:bg-[#CF6679]/10 overline flex items-center gap-2"
                        data-testid="reject-deposit-btn"
                      >
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                    </div>
                  </div>
                ) : payment?.status === "rejected" ? (
                  <div className="text-[#CF6679] text-sm">
                    Rejected. Client notified to retry.
                    <button
                      onClick={() => action(() => http.post(`/admin/clients/${clientId}/deposit/confirm`), "Deposit confirmed")}
                      className="ml-3 overline text-[#D2A853] underline"
                      data-testid="manual-confirm-btn"
                    >
                      Confirm anyway
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-[#A3A8A4]">Client has not marked the deposit as paid yet.</p>
                    <button
                      onClick={() => action(() => http.post(`/admin/clients/${clientId}/deposit/confirm`), "Deposit confirmed")}
                      disabled={busy}
                      className="btn-outline-brass flex items-center gap-2"
                      data-testid="manual-confirm-btn"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Manually Mark Received
                    </button>
                  </div>
                )}
              </Section>

              {/* Appointment */}
              <Section title="Appointment">
                {data.appointment ? (
                  <div className="space-y-2">
                    <div className="text-[#D2A853] font-display text-lg">
                      {new Date(data.appointment.date).toLocaleString()}
                    </div>
                    <div className="text-xs text-[#A3A8A4]">{data.appointment.duration_minutes} min</div>
                    {data.appointment.notes && <p className="text-sm text-[#E8E9E3]">{data.appointment.notes}</p>}
                  </div>
                ) : payment?.status !== "confirmed" ? (
                  <p className="text-sm text-[#A3A8A4]">Confirm deposit before scheduling.</p>
                ) : (
                  <div className="space-y-3">
                    {data.bookings?.length > 0 && (
                      <div className="text-sm">
                        <span className="overline text-[#D2A853]">Client requested:</span>
                        <ul className="mt-2 space-y-1">
                          {data.bookings[0].preferred_dates.map((d, i) => (
                            <li key={i} className="text-[#E8E9E3]">{new Date(d).toLocaleString()}</li>
                          ))}
                        </ul>
                        {data.bookings[0].notes && <p className="text-xs text-[#A3A8A4] mt-2">{data.bookings[0].notes}</p>}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <select
                        value={apptType}
                        onChange={(e) => setApptType(e.target.value)}
                        className="input-line"
                        data-testid="appt-type-select"
                      >
                        <option value="tattoo">Tattoo</option>
                        <option value="consultation">Consultation</option>
                      </select>
                      {apptType === "consultation" && (
                        <select
                          value={apptFormat}
                          onChange={(e) => setApptFormat(e.target.value)}
                          className="input-line"
                          data-testid="appt-format-select"
                        >
                          <option value="phone">Phone</option>
                          <option value="video">Video</option>
                          <option value="in_person">In-person at studio</option>
                        </select>
                      )}
                    </div>
                    <input
                      type="datetime-local"
                      value={apptDate}
                      onChange={(e) => setApptDate(e.target.value)}
                      className="input-line"
                      data-testid="appt-date-input"
                    />
                    <input
                      type="number"
                      value={apptDur}
                      onChange={(e) => setApptDur(parseInt(e.target.value) || 120)}
                      placeholder="Duration in minutes"
                      className="input-line"
                      data-testid="appt-duration-input"
                    />
                    <textarea
                      rows={2}
                      placeholder="Notes (optional)"
                      value={apptNotes}
                      onChange={(e) => setApptNotes(e.target.value)}
                      className="input-line resize-none"
                      data-testid="appt-notes-input"
                    />
                    <button
                      onClick={() => {
                        if (!apptDate) { toast.error("Pick a date"); return; }
                        const body = {
                          date: apptDate,
                          duration_minutes: apptDur,
                          notes: apptNotes,
                          appointment_type: apptType,
                          consultation_format: apptType === "consultation" ? apptFormat : null,
                        };
                        action(() => http.post(`/admin/clients/${clientId}/appointment`, body), "Appointment scheduled");
                      }}
                      disabled={busy}
                      className="btn-brass flex items-center gap-2"
                      data-testid="schedule-appt-btn"
                    >
                      <CalendarClock className="w-4 h-4" /> Schedule
                    </button>
                  </div>
                )}
              </Section>

              {/* Behaviour & Audit */}
              <BehaviourSection
                clientId={clientId}
                user={u}
                highRisk={data.high_risk}
                audit={data.audit || []}
                appointment={data.appointment}
                busy={busy}
                action={action}
              />

              {/* Session Type — flips the client out of consultation phase */}
              <SessionTypeSection
                clientId={clientId}
                user={u}
                payment={data.payment}
                busy={busy}
                action={action}
              />

              {/* Danger Zone — permanent delete */}
              <DangerZone clientId={clientId} user={u} onClose={onClose} onUpdate={onUpdate} />
            </>
          )}

          {tab === "messages" && (
            <div>
              <div className="space-y-3 mb-6 max-h-[60vh] overflow-y-auto pr-2">
                {messages.length === 0 && <p className="text-sm text-[#A3A8A4]">No messages yet.</p>}
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`flex ${m.from_role === "admin" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 ${
                        m.from_role === "admin"
                          ? "bg-[#D2A853]/15 border border-[#D2A853]/40"
                          : "bg-[#1A241E] border border-[#D2A853]/20"
                      }`}
                    >
                      <p className="text-sm text-[#E8E9E3] whitespace-pre-wrap">{m.body}</p>
                      <span className="text-[10px] text-[#A3A8A4] mt-1 block">
                        {m.via === "sms" && "SMS · "}{new Date(m.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!msgBody.trim()) return;
                  action(async () => {
                    await http.post(`/admin/messages/${clientId}`, { body: msgBody });
                    setMsgBody("");
                  }, "Message sent");
                }}
                className="flex gap-3 pt-6 border-t border-[#D2A853]/15"
              >
                <input
                  className="input-line flex-1"
                  placeholder="Reply (will be sent via SMS if configured)..."
                  value={msgBody}
                  onChange={(e) => setMsgBody(e.target.value)}
                  data-testid="admin-message-input"
                />
                <button type="submit" disabled={busy} className="btn-brass flex items-center gap-2" data-testid="admin-message-send">
                  <Send className="w-4 h-4" /> Send
                </button>
              </form>
            </div>
          )}

          {tab === "notes" && (
            <div>
              <div className="space-y-3 mb-6">
                {data.notes.length === 0 && <p className="text-sm text-[#A3A8A4]">No notes yet.</p>}
                {data.notes.map((n) => (
                  <div key={n.id} className="border border-[#D2A853]/20 p-4">
                    <p className="text-sm text-[#E8E9E3] whitespace-pre-wrap">{n.text}</p>
                    <span className="text-[10px] text-[#A3A8A4] mt-2 block">
                      {n.author_name} · {new Date(n.created_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!noteText.trim()) return;
                  action(async () => {
                    await http.post(`/admin/clients/${clientId}/notes`, { text: noteText });
                    setNoteText("");
                  }, "Note added");
                }}
                className="space-y-3 pt-6 border-t border-[#D2A853]/15"
              >
                <textarea
                  rows={3}
                  placeholder="Add a private note..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  className="input-line resize-none"
                  data-testid="admin-note-input"
                />
                <button type="submit" disabled={busy} className="btn-brass flex items-center gap-2" data-testid="admin-note-add">
                  <Plus className="w-4 h-4" /> Add Note
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DrawerTab({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-3 overline whitespace-nowrap transition-colors ${
        active ? "text-[#D2A853] border-b-2 border-[#D2A853]" : "text-[#A3A8A4] hover:text-[#E8E9E3]"
      }`}
    >
      {children}
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-[#D2A853]/15 pt-5">
      <div className="overline text-[#D2A853] mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-[#A3A8A4]">{k}</span>
      <span className="text-[#E8E9E3] text-right">{v}</span>
    </div>
  );
}


function BehaviourSection({ clientId, user, highRisk, audit, appointment, busy, action }) {
  const tNoShow = user.tattoo_no_show_count || 0;
  const tCancel = user.tattoo_cancel_count || 0;
  const cNoShow = user.consultation_no_show_count || 0;
  const cCancel = user.consultation_cancel_count || 0;
  const totalIssues = tNoShow + tCancel + cNoShow + cCancel;
  const override = user.high_risk_override; // true | false | null

  const mark = (kind, action_type) => {
    const apptId = appointment?.id || null;
    const url = action_type === "no_show"
      ? `/admin/clients/${clientId}/mark-no-show`
      : `/admin/clients/${clientId}/mark-cancelled`;
    const label = `${action_type === "no_show" ? "No-show" : "Cancelled"} (${kind}) recorded`;
    action(() => http.post(url, { type: kind, appointment_id: apptId }), label);
  };

  const setOverride = (val) => {
    action(
      () => http.post(`/admin/clients/${clientId}/high-risk-override`, { override: val }),
      val === true ? "Forced High Risk" : val === false ? "Cleared High Risk" : "Reset to auto",
    );
  };

  return (
    <Section title="Behaviour & Audit">
      {/* Counter chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="behaviour-counters">
        {tNoShow > 0 && <Chip>{tNoShow}× No-Show (Tattoo)</Chip>}
        {tCancel > 0 && <Chip>{tCancel}× Cancel (Tattoo)</Chip>}
        {cNoShow > 0 && <Chip>{cNoShow}× No-Show (Consult)</Chip>}
        {cCancel > 0 && <Chip>{cCancel}× Cancel (Consult)</Chip>}
        {totalIssues === 0 && <span className="text-xs text-[#A3A8A4]">No incidents recorded.</span>}
      </div>

      {/* High-risk banner */}
      <div
        className={`flex items-center justify-between gap-4 p-3 mb-4 border ${
          highRisk
            ? "border-[#CF6679]/50 bg-[#CF6679]/5"
            : "border-[#D2A853]/20 bg-transparent"
        }`}
        data-testid="high-risk-banner"
      >
        <div>
          <div className="text-sm font-display text-[#E8E9E3]">
            {highRisk ? "🔻 High Risk" : "Standard"}
          </div>
          <div className="text-xs text-[#A3A8A4] mt-1">
            {override === true && "Manually flagged by admin."}
            {override === false && "Manually cleared (override)."}
            {(override === null || override === undefined) && "Auto rule: 2+ no-shows OR 3+ cancellations."}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {override !== true && (
            <button onClick={() => setOverride(true)} disabled={busy}
                    className="px-3 py-1 text-[10px] uppercase tracking-wider text-[#CF6679] border border-[#CF6679]/40 hover:bg-[#CF6679]/10 transition"
                    data-testid="hr-force">Force</button>
          )}
          {override !== false && (
            <button onClick={() => setOverride(false)} disabled={busy}
                    className="px-3 py-1 text-[10px] uppercase tracking-wider text-[#D2A853] border border-[#D2A853]/40 hover:bg-[#D2A853]/10 transition"
                    data-testid="hr-clear">Clear</button>
          )}
          {override !== null && override !== undefined && (
            <button onClick={() => setOverride(null)} disabled={busy}
                    className="px-3 py-1 text-[10px] uppercase tracking-wider text-[#A3A8A4] border border-[#A3A8A4]/40 hover:bg-[#A3A8A4]/10 transition"
                    data-testid="hr-auto">Auto</button>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-2">
        <BehaviourBtn label="No-Show (Tattoo)"   onClick={() => mark("tattoo", "no_show")} disabled={busy} testId="btn-noshow-tattoo" />
        <BehaviourBtn label="No-Show (Consult)"  onClick={() => mark("consultation", "no_show")} disabled={busy} testId="btn-noshow-consult" />
        <BehaviourBtn label="Cancelled (Tattoo)" onClick={() => mark("tattoo", "cancelled")} disabled={busy} testId="btn-cancel-tattoo" />
        <BehaviourBtn label="Cancelled (Consult)" onClick={() => mark("consultation", "cancelled")} disabled={busy} testId="btn-cancel-consult" />
      </div>

      {/* Audit log */}
      <div className="mt-5">
        <div className="overline text-[#A3A8A4] text-[10px] mb-2">Recent Audit ({audit.length})</div>
        {audit.length === 0 ? (
          <p className="text-xs text-[#A3A8A4]">No actions logged yet.</p>
        ) : (
          <ul className="space-y-2 max-h-48 overflow-y-auto pr-2" data-testid="audit-list">
            {audit.slice(0, 25).map((a) => (
              <li key={a.id} className="text-xs text-[#E8E9E3] border-l border-[#D2A853]/30 pl-3 py-1" data-testid={`audit-row-${a.id}`}>
                <div>
                  <span className="text-[#D2A853]">{a.action.replace(/_/g, " ")}</span>
                  {a.appointment_type && <span className="text-[#A3A8A4]"> · {a.appointment_type}</span>}
                </div>
                <div className="text-[10px] text-[#A3A8A4]">
                  {new Date(a.created_at).toLocaleString()} · {a.admin_email || "system"}
                  {a.notes && ` · ${a.notes}`}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-wider border border-[#D2A853]/40 text-[#D2A853]">
      {children}
    </span>
  );
}

function BehaviourBtn({ label, onClick, disabled, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="px-3 py-2 text-xs text-[#E8E9E3] border border-[#D2A853]/30 hover:border-[#D2A853]/70 hover:bg-[#D2A853]/5 transition disabled:opacity-50"
      data-testid={testId}
    >
      {label}
    </button>
  );
}


function SessionTypeSection({ clientId, user, payment, busy, action }) {
  const current = user.session_type || null;
  const paid = payment?.status === "confirmed";

  const TIERS = [
    { value: "small",    label: "Small Session",  sub: "1–3 hours · $100 deposit" },
    { value: "half_day", label: "Half-Day",        sub: "4 hours · $250 deposit"  },
    { value: "full_day", label: "Full-Day",        sub: "8 hours · $500 deposit"  },
  ];

  const set = (val) => {
    action(
      () => http.post(`/admin/clients/${clientId}/session-type`, { session_type: val }),
      val ? `Session type set to ${val.replace("_", "-")}` : "Session type cleared",
    );
  };

  return (
    <Section title="Session Type">
      <p className="text-xs text-[#A3A8A4] mb-4 leading-relaxed">
        Assign after the consultation. This unlocks the deposit step on the client&rsquo;s portal —
        the deposit amount is auto-derived from the tier you choose.
        {paid && current && (
          <span className="block mt-2 text-[#D2A853]">
            Deposit already confirmed for the current tier; changes won&rsquo;t re-charge.
          </span>
        )}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        {TIERS.map((t) => (
          <button
            key={t.value}
            onClick={() => set(t.value)}
            disabled={busy}
            className={`text-left px-3 py-3 border transition disabled:opacity-50 ${
              current === t.value
                ? "border-[#D2A853] bg-[#D2A853]/10"
                : "border-[#D2A853]/30 hover:border-[#D2A853]/70 hover:bg-[#D2A853]/5"
            }`}
            data-testid={`session-type-${t.value}`}
          >
            <div className="text-xs uppercase tracking-wider text-[#D2A853]">{t.label}</div>
            <div className="text-[10px] text-[#A3A8A4] mt-1">{t.sub}</div>
          </button>
        ))}
      </div>
      {current && (
        <button
          onClick={() => set(null)}
          disabled={busy}
          className="text-[10px] uppercase tracking-wider text-[#A3A8A4] hover:text-[#CF6679] transition disabled:opacity-50"
          data-testid="session-type-clear"
        >
          Clear (return to consultation phase)
        </button>
      )}
    </Section>
  );
}



function ContactSection({ user, reload }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: user.name, email: user.email, phone: user.phone || "" });
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { setForm({ name: user.name, email: user.email, phone: user.phone || "" }); }, [user.id, user.name, user.email, user.phone]);

  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/admin/clients/${user.id}`, form);
      toast.success("Updated");
      setEdit(false);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!edit) {
    return (
      <Section title={
        <span className="flex items-center justify-between">
          Contact
          <button onClick={() => setEdit(true)} className="overline text-[10px] text-[#A3A8A4] hover:text-[#D2A853]" data-testid="contact-edit-toggle">
            Edit
          </button>
        </span>
      }>
        <Row k="Phone" v={user.phone || "—"} />
        <Row k="Email" v={user.email} />
        <Row k="Joined" v={new Date(user.created_at).toLocaleDateString()} />
        {user.imported_from && <Row k="Source" v={user.imported_from === "schedulista_csv" ? "Schedulista import" : user.imported_from} />}
      </Section>
    );
  }
  return (
    <Section title="Edit Contact">
      <div className="space-y-3">
        <input className="input-line" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-edit-name" />
        <input className="input-line" placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-edit-email" />
        <input className="input-line" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-edit-phone" />
        <div className="flex gap-2 pt-2">
          <button onClick={save} disabled={busy} className="btn-brass flex-1 flex items-center justify-center gap-2" data-testid="contact-edit-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
          <button onClick={() => setEdit(false)} className="btn-outline-brass" data-testid="contact-edit-cancel">Cancel</button>
        </div>
      </div>
    </Section>
  );
}

function InviteSection({ user, reload }) {
  const [busy, setBusy] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const isImported = user.status === "imported_unclaimed" || (user.imported_from && !user.claimed_at);
  const isDeactivated = user.is_active === false && user.status !== "imported_unclaimed";

  const fetchLink = async () => {
    setBusy(true);
    try {
      const { data } = await http.get(`/admin/clients/${user.id}/invite-link`);
      setInviteUrl(data.invite_url);
      try {
        await navigator.clipboard.writeText(data.invite_url);
        toast.success("Invite link copied to clipboard");
      } catch (_) { toast.success("Link ready — copy manually below"); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/admin/clients/${user.id}/resend-invite`);
      setInviteUrl(data.invite_url);
      toast.success("Invite email sent (mock if Resend not configured)");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const activate = async () => {
    setBusy(true);
    try {
      await http.post(`/admin/clients/${user.id}/activate`);
      toast.success("Activated");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const deactivate = async () => {
    if (!window.confirm("Deactivate this client? They won't be able to log in.")) return;
    setBusy(true);
    try {
      await http.post(`/admin/clients/${user.id}/deactivate`);
      toast.success("Deactivated");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!isImported && !isDeactivated && user.is_active !== false) {
    // Active claimed client — show only deactivate
    return (
      <Section title="Account">
        <div className="flex items-center justify-between">
          <span className="text-sm text-[#4CAF50]">Active · claimed{user.claimed_at ? ` ${new Date(user.claimed_at).toLocaleDateString()}` : ""}</span>
          <button onClick={deactivate} disabled={busy} className="overline text-[10px] text-[#CF6679] hover:text-[#CF6679]" data-testid="account-deactivate">
            Deactivate
          </button>
        </div>
      </Section>
    );
  }

  return (
    <Section title="Invite / Account">
      <div className="space-y-3">
        {isImported && (
          <div className="text-sm text-[#E2BC6A] flex items-center gap-2">
            <Mail className="w-4 h-4" /> Imported · Unclaimed
          </div>
        )}
        {isDeactivated && <div className="text-sm text-[#CF6679]">Deactivated</div>}
        {user.invite_sent_at && <div className="text-xs text-[#A3A8A4]">Last invite sent: {new Date(user.invite_sent_at).toLocaleString()}</div>}
        {inviteUrl && (
          <div className="bg-[#1A241E] border border-[#D2A853]/30 p-3 break-all text-xs text-[#D2A853] font-mono" data-testid="invite-url-display">
            {inviteUrl}
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-2">
          {isImported && (
            <>
              <button onClick={fetchLink} disabled={busy} className="btn-outline-brass !px-4 !py-2 text-xs flex items-center gap-2" data-testid="invite-copy-link">
                Copy Link
              </button>
              <button onClick={resend} disabled={busy} className="btn-outline-brass !px-4 !py-2 text-xs flex items-center gap-2" data-testid="invite-resend">
                Resend Invite Email
              </button>
            </>
          )}
          {isDeactivated && (
            <button onClick={activate} disabled={busy} className="btn-brass !px-4 !py-2 text-xs flex items-center gap-2" data-testid="account-activate">
              Activate
            </button>
          )}
        </div>
      </div>
    </Section>
  );
}

function NotificationsPanel({ notifs, reload, onOpenClient }) {
  const markAllRead = async () => {
    await http.post("/admin/notifications/read-all");
    reload();
  };
  return (
    <div className="frame-card p-8" data-testid="notifications-panel">
      <div className="flex items-center justify-between mb-6">
        <span className="overline text-[#D2A853]">All Notifications</span>
        {notifs.unread > 0 && (
          <button onClick={markAllRead} className="overline text-[#A3A8A4] hover:text-[#D2A853]" data-testid="mark-all-read">
            Mark all read
          </button>
        )}
      </div>
      {notifs.items.length === 0 && <p className="text-sm text-[#A3A8A4]">No notifications.</p>}
      <div className="space-y-3">
        {notifs.items.map((n) => (
          <div
            key={n.id}
            onClick={() => {
              http.post(`/admin/notifications/${n.id}/read`).then(reload);
              if (n.client_id) onOpenClient(n.client_id);
            }}
            className={`p-4 border cursor-pointer transition ${
              n.is_read ? "border-[#D2A853]/15 opacity-70" : "border-[#D2A853]/40 bg-[#D2A853]/5 hover:bg-[#D2A853]/10"
            }`}
            data-testid={`notification-${n.id}`}
          >
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-[#D2A853] mt-0.5" strokeWidth={1.4} />
              <div className="flex-1">
                <div className="font-medium text-[#E8E9E3] text-sm">{n.title}</div>
                <p className="text-sm text-[#A3A8A4] mt-1">{n.body}</p>
                <span className="text-[10px] text-[#A3A8A4] mt-2 block">{new Date(n.created_at).toLocaleString()}</span>
              </div>
              {!n.is_read && <span className="w-2 h-2 bg-[#D2A853] rounded-full mt-2" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  useEffect(() => {
    http.get("/admin/settings").then(({ data }) => setS(data)).catch(() => {});
  }, []);

  const save = async () => {
    // Client-side validation for portal URL
    const pu = (s.portal_url || "").trim().replace(/\/$/, "");
    if (pu) {
      if (!pu.startsWith("https://")) {
        toast.error("Public Portal URL must start with https://");
        return;
      }
      try {
        const parsed = new URL(pu);
        const host = (parsed.hostname || "").toLowerCase();
        if (!host.includes(".") || host === "localhost" || host.endsWith(".local") || host.endsWith(".localhost") || /^\d+\.\d+\.\d+\.\d+$/.test(host) || host === "0.0.0.0") {
          toast.error("Public Portal URL must be a real public domain (no localhost or IPs)");
          return;
        }
        if (parsed.pathname && parsed.pathname !== "/") {
          toast.error("Public Portal URL must not include a path — just the domain");
          return;
        }
      } catch (_) {
        toast.error("Public Portal URL is not a valid URL");
        return;
      }
    }
    setBusy(true);
    try {
      const { data } = await http.put("/admin/settings", { ...s, portal_url: pu });
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  if (!s) return <Loader2 className="w-6 h-6 animate-spin text-[#D2A853]" />;

  const upd = (k, v) => setS({ ...s, [k]: v });

  const importAppointments = async (file) => {
    if (!file) return;
    setCsvBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post(`/admin/import/appointments-csv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.imported || 0} appointments`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setCsvBusy(false); }
  };

  const previewClients = async (file) => {
    if (!file) return;
    setPreviewBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post(`/admin/import/clients-csv/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // mark all will_create as selected by default
      const initialSelected = new Set(data.rows.filter((r) => r.status === "will_create").map((r) => r.index));
      setImportPreview({ ...data, selected: initialSelected, sendInvites: true });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setPreviewBusy(false); }
  };

  const commitImport = async () => {
    if (!importPreview) return;
    setCsvBusy(true);
    try {
      const rows = importPreview.rows.filter((r) => importPreview.selected.has(r.index) && r.status === "will_create");
      const { data } = await http.post(`/admin/import/clients-csv/commit`, {
        rows,
        send_invites: importPreview.sendInvites,
      });
      toast.success(`Created ${data.created} clients · ${data.invited} invites sent`);
      setImportPreview(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setCsvBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="frame-card p-8">
        <span className="overline text-[#D2A853]">Studio</span>
        <div className="mt-6 space-y-4">
          <SField label="Studio Name">
            <input className="input-line" value={s.studio_name || ""} onChange={(e) => upd("studio_name", e.target.value)} data-testid="settings-studio-name" />
          </SField>
          <SField label="Deposit by Session Type (USD)">
            <div className="grid grid-cols-3 gap-3">
              <input type="number" className="input-line" placeholder="Small (1-3hr)"
                     value={s.deposit_small ?? 100} onChange={(e) => upd("deposit_small", parseFloat(e.target.value) || 0)}
                     data-testid="settings-deposit-small" />
              <input type="number" className="input-line" placeholder="Half-day (4hr)"
                     value={s.deposit_half_day ?? 250} onChange={(e) => upd("deposit_half_day", parseFloat(e.target.value) || 0)}
                     data-testid="settings-deposit-half-day" />
              <input type="number" className="input-line" placeholder="Full-day (8hr)"
                     value={s.deposit_full_day ?? 500} onChange={(e) => upd("deposit_full_day", parseFloat(e.target.value) || 0)}
                     data-testid="settings-deposit-full-day" />
            </div>
            <p className="text-xs text-[#A3A8A4] mt-2">
              Small (1–3 hr) · Half-day (4 hr) · Full-day (8 hr). Deposit amount is auto-applied
              once you assign a session type to a client.
            </p>
          </SField>
          <SField label="Pricing Display (shown on the consultation page)">
            <div className="grid grid-cols-3 gap-3">
              <input type="number" className="input-line" placeholder="Hourly"
                     value={s.rate_hourly ?? 250} onChange={(e) => upd("rate_hourly", parseFloat(e.target.value) || 0)}
                     data-testid="settings-rate-hourly" />
              <input type="number" className="input-line" placeholder="Half-day"
                     value={s.rate_half_day ?? 800} onChange={(e) => upd("rate_half_day", parseFloat(e.target.value) || 0)}
                     data-testid="settings-rate-half-day" />
              <input type="number" className="input-line" placeholder="Full-day"
                     value={s.rate_full_day ?? 1500} onChange={(e) => upd("rate_full_day", parseFloat(e.target.value) || 0)}
                     data-testid="settings-rate-full-day" />
            </div>
            <p className="text-xs text-[#A3A8A4] mt-2">
              Hourly · Half-day · Full-day rates shown to clients during the consultation phase.
            </p>
          </SField>
          <SField label="Studio Address (revealed only after deposit confirmed)">
            <textarea rows={3} className="input-line resize-none" placeholder="123 Main St, Suite 4&#10;City, State ZIP"
              value={s.studio_address || ""} onChange={(e) => upd("studio_address", e.target.value)} data-testid="settings-studio-address" />
          </SField>
          <SField label="About Text (shown on landing page)">
            <textarea rows={4} className="input-line resize-none" value={s.about_text || ""} onChange={(e) => upd("about_text", e.target.value)} data-testid="settings-about-text" />
          </SField>
          <SField label="Tagline (small text under main logo)">
            <input className="input-line" value={s.tagline || ""} onChange={(e) => upd("tagline", e.target.value)} data-testid="settings-tagline" />
          </SField>
          <SField label="Public Contact Email (footer link)">
            <input className="input-line" type="email" placeholder="hello@paiddewztattoo.com" value={s.contact_email || ""} onChange={(e) => upd("contact_email", e.target.value)} data-testid="settings-contact-email" />
          </SField>
          <SField label="Instagram URL (footer link)">
            <input className="input-line" placeholder="https://instagram.com/paiddewz" value={s.instagram_url || ""} onChange={(e) => upd("instagram_url", e.target.value)} data-testid="settings-instagram-url" />
          </SField>
          <SField label="Pre-appointment Instructions (in confirmation email)">
            <textarea rows={3} className="input-line resize-none" value={s.appointment_instructions || ""} onChange={(e) => upd("appointment_instructions", e.target.value)} data-testid="settings-appt-instructions" />
          </SField>
          <SField label="Public Portal URL — used in ALL client emails &amp; invite links">
            <input
              className="input-line"
              placeholder="https://book.paiddewztattoo.com"
              value={s.portal_url || ""}
              onChange={(e) => upd("portal_url", e.target.value)}
              data-testid="settings-portal-url"
            />
            <PortalUrlHelper value={s.portal_url || ""} />
          </SField>
          <SField label="Sender Email (Resend)">
            <input className="input-line" placeholder="onboarding@resend.dev or studio@yourdomain.com" value={s.sender_email || ""} onChange={(e) => upd("sender_email", e.target.value)} data-testid="settings-sender-email" />
            <p className="text-xs text-[#A3A8A4] mt-2">Set <code className="text-[#D2A853]">RESEND_API_KEY</code> in backend .env to activate emails. Verify your domain in Resend before using a custom from address.</p>
          </SField>
          <SField label="Legacy Schedulista Link (transition only)">
            <input className="input-line" placeholder="https://schedulista.com/..." value={s.legacy_schedulista_link || ""} onChange={(e) => upd("legacy_schedulista_link", e.target.value)} data-testid="settings-schedulista-link" />
          </SField>
          <SField label="Landing Page · Limited Availability CTA">
            <label className="flex items-center gap-3 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!s.show_availability_cta}
                onChange={(e) => upd("show_availability_cta", e.target.checked)}
                className="accent-[#D2A853]"
                data-testid="settings-show-availability-cta"
              />
              <span className="text-sm text-[#E8E9E3]">Show subtle "limited openings" CTA on the landing page</span>
            </label>
            <p className="text-xs text-[#A3A8A4] mt-1">Only opening types are shown publicly — never exact dates.</p>
          </SField>
        </div>
      </div>
      <div className="frame-card p-8">
        <span className="overline text-[#D2A853]">Payment Links</span>
        <div className="mt-6 space-y-4">
          <SField label="Square Link"><input className="input-line" placeholder="https://square.link/..." value={s.square_link || ""} onChange={(e) => upd("square_link", e.target.value)} data-testid="settings-square" /></SField>
          <SField label="PayPal Link"><input className="input-line" placeholder="https://paypal.me/..." value={s.paypal_link || ""} onChange={(e) => upd("paypal_link", e.target.value)} data-testid="settings-paypal" /></SField>
          <SField label="Venmo Link"><input className="input-line" placeholder="https://venmo.com/..." value={s.venmo_link || ""} onChange={(e) => upd("venmo_link", e.target.value)} data-testid="settings-venmo" /></SField>
          <SField label="Zelle Info"><input className="input-line" placeholder="email or phone" value={s.zelle_info || ""} onChange={(e) => upd("zelle_info", e.target.value)} data-testid="settings-zelle" /></SField>
          <SField label="Deposit Instructions">
            <textarea rows={3} className="input-line resize-none" value={s.deposit_instructions || ""} onChange={(e) => upd("deposit_instructions", e.target.value)} data-testid="settings-instructions" />
          </SField>
        </div>
        <div className="border-t border-[#D2A853]/15 mt-6 pt-6">
          <span className="overline text-[#D2A853]">Schedulista Migration</span>
          <p className="text-xs text-[#A3A8A4] mt-2 mb-4">Best-effort import from CSV exports. Headers are auto-detected.</p>
          <div className="space-y-3">
            <label className="flex items-center gap-3 p-3 border border-dashed border-[#D2A853]/30 hover:bg-[#D2A853]/5 cursor-pointer">
              <Upload className="w-4 h-4 text-[#D2A853]" />
              <span className="text-sm text-[#E8E9E3]">Preview Clients CSV</span>
              <input type="file" accept=".csv" className="hidden" onChange={(e) => previewClients(e.target.files?.[0])} disabled={previewBusy} data-testid="csv-clients" />
              {previewBusy && <Loader2 className="w-4 h-4 animate-spin text-[#D2A853] ml-auto" />}
            </label>
            <label className="flex items-center gap-3 p-3 border border-dashed border-[#D2A853]/30 hover:bg-[#D2A853]/5 cursor-pointer">
              <Upload className="w-4 h-4 text-[#D2A853]" />
              <span className="text-sm text-[#E8E9E3]">Import Appointments CSV</span>
              <input type="file" accept=".csv" className="hidden" onChange={(e) => importAppointments(e.target.files?.[0])} disabled={csvBusy} data-testid="csv-appointments" />
            </label>
          </div>
        </div>
      </div>
      <div className="lg:col-span-2">
        <FaqEditor faqs={s.faqs || []} onChange={(faqs) => upd("faqs", faqs)} />
      </div>
      <div className="lg:col-span-2">
        <div className="frame-card p-8" data-testid="reminders-policy-panel">
          <span className="overline text-[#D2A853]">Reminders &amp; Cancellation</span>
          <p className="text-xs text-[#A3A8A4] mt-2 mb-6 max-w-md">
            Email + SMS reminders are always on. Set the lead times and the policy text shown to clients.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <SField label="Reminder Lead Time (days)">
              <input
                type="number"
                min="1"
                max="14"
                className="input-line"
                value={s.reminder_lead_days ?? 3}
                onChange={(e) => upd("reminder_lead_days", parseInt(e.target.value, 10) || 3)}
                data-testid="settings-reminder-lead-days"
              />
            </SField>
            <SField label="Last-Minute SMS (hours)">
              <input
                type="number"
                min="1"
                max="24"
                className="input-line"
                value={s.last_minute_sms_hours ?? 3}
                onChange={(e) => upd("last_minute_sms_hours", parseInt(e.target.value, 10) || 3)}
                data-testid="settings-last-minute-sms-hours"
              />
            </SField>
            <SField label="Time Zone Label">
              <input
                className="input-line"
                placeholder="MT"
                value={s.timezone_label || ""}
                onChange={(e) => upd("timezone_label", e.target.value)}
                data-testid="settings-timezone-label"
              />
            </SField>
          </div>
          <div className="mt-6">
            <SField label="Custom Reminder Message (shown in emails + portal)">
              <textarea
                rows={2}
                className="input-line resize-none"
                placeholder="Optional — leave blank for none"
                value={s.reminder_custom_message || ""}
                onChange={(e) => upd("reminder_custom_message", e.target.value)}
                data-testid="settings-reminder-custom-message"
              />
            </SField>
          </div>
          <div className="mt-6">
            <SField label="Cancellation Policy (tattoo only)">
              <textarea
                rows={6}
                className="input-line resize-none"
                value={s.cancellation_policy || ""}
                onChange={(e) => upd("cancellation_policy", e.target.value)}
                data-testid="settings-cancellation-policy"
              />
            </SField>
          </div>
        </div>
      </div>
      <div className="lg:col-span-2 flex justify-end">
        <button onClick={save} disabled={busy} className="btn-brass flex items-center gap-2" data-testid="settings-save">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save Settings
        </button>
      </div>

      {/* Danger zone — studio-wide data cleanup */}
      <div className="lg:col-span-2 mt-10">
        <DataCleanupPanel />
      </div>

      {importPreview && (
        <ImportPreviewModal
          data={importPreview}
          busy={csvBusy}
          onChange={setImportPreview}
          onCommit={commitImport}
          onClose={() => setImportPreview(null)}
        />
      )}
    </div>
  );
}

function ImportPreviewModal({ data, busy, onChange, onCommit, onClose }) {
  const toggleRow = (idx) => {
    const next = new Set(data.selected);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    onChange({ ...data, selected: next });
  };
  const willCreate = data.rows.filter((r) => r.status === "will_create");
  const allSelected = willCreate.every((r) => data.selected.has(r.index));
  const toggleAll = () => {
    const next = new Set(data.selected);
    if (allSelected) willCreate.forEach((r) => next.delete(r.index));
    else willCreate.forEach((r) => next.add(r.index));
    onChange({ ...data, selected: next });
  };

  return (
    <div className="fixed inset-0 z-40 bg-[#0A120E]/85 backdrop-blur-sm flex items-center justify-center p-4 lg:col-span-2" onClick={onClose}>
      <div className="frame-card w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="import-preview">
        <div className="px-6 py-4 border-b border-[#D2A853]/20 flex items-center justify-between">
          <div>
            <span className="overline text-[#D2A853]">CSV Import Preview</span>
            <h3 className="font-display text-2xl text-[#E8E9E3] mt-1">
              {data.selected.size} selected · {data.summary.total} total
            </h3>
          </div>
          <button onClick={onClose} className="text-[#A3A8A4] hover:text-[#D2A853]" data-testid="import-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex flex-wrap gap-3 px-6 py-3 border-b border-[#D2A853]/15 text-xs">
          <Tag color="text-[#D2A853]" label={`${data.summary.will_create} will create`} />
          <Tag color="text-[#E2BC6A]" label={`${data.summary.duplicate_email} dup email`} />
          <Tag color="text-[#E2BC6A]" label={`${data.summary.duplicate_phone} dup phone`} />
          {data.summary.invalid > 0 && <Tag color="text-[#CF6679]" label={`${data.summary.invalid} invalid`} />}
          <button onClick={toggleAll} className="ml-auto overline text-[#A3A8A4] hover:text-[#D2A853]" data-testid="import-toggle-all">
            {allSelected ? "Deselect All" : "Select All Eligible"}
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className="overline text-[#A3A8A4] py-2 w-8"></th>
                <th className="overline text-[#A3A8A4] py-2">Name</th>
                <th className="overline text-[#A3A8A4] py-2">Email</th>
                <th className="overline text-[#A3A8A4] py-2 hidden sm:table-cell">Phone</th>
                <th className="overline text-[#A3A8A4] py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.index} className="border-t border-[#D2A853]/10">
                  <td className="py-3">
                    <input
                      type="checkbox"
                      disabled={r.status !== "will_create"}
                      checked={data.selected.has(r.index)}
                      onChange={() => toggleRow(r.index)}
                      className="accent-[#D2A853]"
                      data-testid={`import-row-${r.index}`}
                    />
                  </td>
                  <td className="py-3 text-[#E8E9E3]">{r.name}</td>
                  <td className="py-3 text-[#A3A8A4]">{r.email}</td>
                  <td className="py-3 text-[#A3A8A4] hidden sm:table-cell">{r.phone || "—"}</td>
                  <td className="py-3">
                    <RowStatus status={r.status} reason={r.reason} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-[#D2A853]/20 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!data.sendInvites}
              onChange={(e) => onChange({ ...data, sendInvites: e.target.checked })}
              className="accent-[#D2A853]"
              data-testid="import-send-invites"
            />
            <span className="text-[#E8E9E3]">Send invite emails on commit</span>
          </label>
          <button onClick={onCommit} disabled={busy || data.selected.size === 0} className="btn-brass flex items-center gap-2" data-testid="import-commit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Commit Import ({data.selected.size})
          </button>
        </div>
      </div>
    </div>
  );
}

function RowStatus({ status, reason }) {
  if (status === "will_create") return <span className="overline text-[10px] text-[#D2A853]">Will create</span>;
  if (status === "duplicate_email") return <span className="overline text-[10px] text-[#E2BC6A]" title={reason}>Dup email</span>;
  if (status === "duplicate_phone") return <span className="overline text-[10px] text-[#E2BC6A]" title={reason}>Dup phone</span>;
  return <span className="overline text-[10px] text-[#CF6679]" title={reason}>Invalid</span>;
}

function Tag({ color, label }) {
  return <span className={`overline ${color}`}>{label}</span>;
}

function SField({ label, children }) {
  return (
    <div>
      <label className="overline text-[#A3A8A4] block mb-2">{label}</label>
      {children}
    </div>
  );
}

function FaqEditor({ faqs, onChange }) {
  const update = (idx, key, val) => {
    const next = [...faqs];
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };
  const add = () => onChange([...faqs, { q: "", a: "" }]);
  const remove = (idx) => onChange(faqs.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= faqs.length) return;
    const next = [...faqs];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div className="frame-card p-8" data-testid="faq-editor">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <span className="overline text-[#D2A853]">Frequently Asked Questions</span>
          <p className="text-xs text-[#A3A8A4] mt-2 max-w-md">
            FAQ section appears on the public landing only when at least one question is filled in.
            Leave empty to hide the section entirely.
          </p>
        </div>
        <button
          type="button"
          onClick={add}
          className="btn-outline-brass !px-4 !py-2 !text-xs inline-flex items-center gap-2 shrink-0"
          data-testid="faq-add"
        >
          <Plus className="w-3 h-3" /> Add Question
        </button>
      </div>

      {faqs.length === 0 ? (
        <div className="mt-6 border border-dashed border-[#D2A853]/25 p-8 text-center">
          <span className="text-xs text-[#A3A8A4]">No questions yet. The FAQ section is hidden on the public landing.</span>
        </div>
      ) : (
        <div className="mt-6 space-y-5">
          {faqs.map((f, idx) => (
            <div key={idx} className="border border-[#D2A853]/15 p-5" data-testid={`faq-row-${idx}`}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <span className="overline text-[#A3A8A4] text-[10px]">Question {idx + 1}</span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                          className="p-1 text-[#A3A8A4] hover:text-[#D2A853] disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`faq-move-up-${idx}`} aria-label="Move up">
                    <MoveUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === faqs.length - 1}
                          className="p-1 text-[#A3A8A4] hover:text-[#D2A853] disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`faq-move-down-${idx}`} aria-label="Move down">
                    <MoveDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => remove(idx)}
                          className="p-1 text-[#A3A8A4] hover:text-[#CF6679] ml-1"
                          data-testid={`faq-remove-${idx}`} aria-label="Remove question">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <input
                type="text"
                className="input-line"
                placeholder="Question"
                value={f.q || ""}
                onChange={(e) => update(idx, "q", e.target.value)}
                data-testid={`faq-q-${idx}`}
              />
              <textarea
                rows={3}
                className="input-line resize-none mt-3"
                placeholder="Answer"
                value={f.a || ""}
                onChange={(e) => update(idx, "a", e.target.value)}
                data-testid={`faq-a-${idx}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PortalUrlHelper({ value }) {
  const v = (value || "").trim().replace(/\/$/, "");
  let issue = null;
  let warn = null;
  if (v) {
    if (!v.startsWith("https://")) {
      issue = "Must start with https://";
    } else {
      try {
        const u = new URL(v);
        const host = (u.hostname || "").toLowerCase();
        if (!host.includes(".") || host === "localhost" || host.endsWith(".local") || host.endsWith(".localhost") || /^\d+\.\d+\.\d+\.\d+$/.test(host) || host === "0.0.0.0") {
          issue = "Must be a real public domain (no localhost or IPs)";
        } else if (u.pathname && u.pathname !== "/") {
          issue = "Must not include a path — just the domain";
        } else if (u.search || u.hash) {
          issue = "Must not include query string or fragment";
        } else if (host.includes("preview.emergentagent.com") || host.includes("emergentagent.com")) {
          warn = "This is the QA preview URL. Switch to your real domain before going live.";
        }
      } catch (_) { issue = "Not a valid URL"; }
    }
  }

  return (
    <div className="mt-2">
      {issue && (
        <div className="flex items-start gap-2 text-xs text-[#CF6679]" data-testid="portal-url-error">
          <span aria-hidden>✕</span>
          <span>{issue}</span>
        </div>
      )}
      {!issue && warn && (
        <div className="flex items-start gap-2 text-xs text-[#E2BC6A]" data-testid="portal-url-warning">
          <span aria-hidden>⚠</span>
          <span>{warn}</span>
        </div>
      )}
      {!issue && !warn && v && (
        <div className="flex items-start gap-2 text-xs text-[#4CAF50]" data-testid="portal-url-ok">
          <span aria-hidden>✓</span>
          <span>Looks good. Will be used in every client email and invite link.</span>
        </div>
      )}
      <p className="text-xs text-[#A3A8A4] mt-2 leading-relaxed">
        Set this to your branded domain (e.g. <span className="text-[#D2A853]">paiddewztattoo.com</span>, <span className="text-[#D2A853]">portal.paiddewztattoo.com</span>, or <span className="text-[#D2A853]">book.paiddewztattoo.com</span>). Every invite link, deposit confirmation email, and appointment confirmation email will use this exact URL. <strong className="text-[#E2BC6A]">Do not use the temporary preview URL with real clients.</strong>
      </p>
    </div>
  );
}

function BookingRequestsPanel({ onOpenClient, reloadAll }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/bookings", { params: { status: "requested" } });
      setRequests(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirm = async (booking, slotId) => {
    setBusy(true);
    try {
      await http.post(`/admin/bookings/${booking.id}/confirm-slot`, { slot_id: slotId });
      toast.success(`Confirmed appointment with ${booking.client.name}`);
      load(); reloadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const rejectAll = async (booking) => {
    if (!window.confirm(`Reject all preferred slots for ${booking.client.name}?`)) return;
    setBusy(true);
    try {
      await http.post(`/admin/bookings/${booking.id}/reject-all`);
      toast.success("Client asked to pick new dates");
      load(); reloadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#D2A853]" />;
  if (requests.length === 0) {
    return (
      <div className="frame-card p-12 text-center" data-testid="requests-empty">
        <Inbox className="w-8 h-8 mx-auto text-[#A3A8A4]/40 mb-4" strokeWidth={1.2} />
        <p className="text-[#A3A8A4]">No pending booking requests.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="booking-requests">
      {requests.map((b) => (
        <div key={b.id} className="frame-card p-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <button onClick={() => onOpenClient(b.user_id)} className="font-display text-2xl text-[#E8E9E3] hover:text-[#D2A853]" data-testid={`req-client-${b.id}`}>
                {b.client?.name || "—"}
              </button>
              <div className="text-sm text-[#A3A8A4]">{b.client?.email} · {b.client?.phone}</div>
            </div>
            <span className="overline text-[#D2A853] text-[10px]">
              Requested {new Date(b.created_at).toLocaleString()}
            </span>
          </div>
          {b.notes && <p className="text-sm text-[#E8E9E3] mb-4 p-3 bg-[#D2A853]/5 border-l-2 border-[#D2A853]/40">{b.notes}</p>}
          <div className="space-y-2">
            <span className="overline text-[#A3A8A4] text-[10px]">Preferred Options</span>
            {b.slot_ids?.map((sid, i) => (
              <BookingSlotChoice
                key={sid}
                slotId={sid}
                label={b.preferred_labels?.[i]}
                date={b.preferred_dates?.[i]}
                onConfirm={() => confirm(b, sid)}
                disabled={busy}
              />
            ))}
          </div>
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-[#D2A853]/15">
            <button onClick={() => onOpenClient(b.user_id)} className="overline text-[#A3A8A4] hover:text-[#D2A853]" data-testid={`req-open-profile-${b.id}`}>
              View Client Profile →
            </button>
            <button onClick={() => rejectAll(b)} disabled={busy} className="overline text-[#CF6679] hover:text-[#CF6679] flex items-center gap-2" data-testid={`req-reject-${b.id}`}>
              <XCircle className="w-3 h-3" /> Reject All — Ask for New Dates
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function BookingSlotChoice({ slotId, label, date, onConfirm, disabled }) {
  return (
    <div className="flex items-center justify-between gap-3 p-3 border border-[#D2A853]/20 hover:border-[#D2A853]/50">
      <div className="min-w-0">
        <div className="text-sm text-[#E8E9E3]">{label}</div>
        <div className="text-xs text-[#A3A8A4]">{date ? new Date(date).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" }) : ""}</div>
      </div>
      <button
        onClick={onConfirm}
        disabled={disabled}
        className="btn-brass !px-4 !py-2 flex items-center gap-2 whitespace-nowrap"
        data-testid={`confirm-slot-${slotId}`}
      >
        <CheckCircle2 className="w-3.5 h-3.5" /> Confirm
      </button>
    </div>
  );
}


function FeaturedWorkPanel() {
  const [hero, setHero] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [{ data: h }, { data: g }] = await Promise.all([
        http.get("/admin/featured-work", { params: { kind: "hero" } }),
        http.get("/admin/featured-work", { params: { kind: "gallery" } }),
      ]);
      setHero(h.find((x) => x.is_visible) || h[0] || null);
      setGallery(g);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await http.post(`/admin/featured-work/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${kind === "hero" ? "Hero" : "Gallery"} image uploaded`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const toggleVisible = async (item) => {
    try {
      await http.patch(`/admin/featured-work/${item.id}`, { is_visible: !item.is_visible });
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await http.delete(`/admin/featured-work/${item.id}`);
      toast.success("Deleted");
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const move = async (idx, dir) => {
    const next = [...gallery];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setGallery(next);
    try {
      await http.post("/admin/featured-work/reorder", { ids: next.map((x) => x.id) });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); loadAll(); }
  };

  const fileSrc = (id) => `${API}/public/files/${id}`;

  return (
    <div className="space-y-8" data-testid="featured-work-panel">
      <div className="frame-card p-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="overline text-[#D2A853]">Studio Hero Image</span>
            <p className="text-xs text-[#A3A8A4] mt-1">Shown on the landing page hero. Replaces previous hero on upload.</p>
          </div>
          <label className="btn-outline-brass !px-4 !py-2 text-xs cursor-pointer flex items-center gap-2">
            <Upload className="w-3 h-3" /> {hero ? "Replace" : "Upload"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload("hero", e.target.files?.[0])} disabled={busy} data-testid="upload-hero" />
          </label>
        </div>
        {hero ? (
          <div className="aspect-video max-w-md frame-card overflow-hidden">
            <img src={fileSrc(hero.file_id)} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="aspect-video max-w-md frame-card bg-[#2A3A30] flex items-center justify-center">
            <span className="overline text-[#A3A8A4]/60 text-[10px]">No hero image — placeholder shown publicly</span>
          </div>
        )}
      </div>

      <div className="frame-card p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="overline text-[#D2A853]">Featured Gallery</span>
            <p className="text-xs text-[#A3A8A4] mt-1">Up to 6 tiles shown on landing. Reorder, hide, or remove anytime.</p>
          </div>
          <label className="btn-brass !px-4 !py-2 text-xs cursor-pointer flex items-center gap-2">
            <Plus className="w-3 h-3" /> Add
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload("gallery", e.target.files?.[0])} disabled={busy} data-testid="upload-gallery" />
          </label>
        </div>
        {gallery.length === 0 ? (
          <div className="text-center py-8 text-sm text-[#A3A8A4]">No featured pieces yet — upload your first.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {gallery.map((item, i) => (
              <div key={item.id} className={`relative aspect-square frame-card overflow-hidden ${item.is_visible ? "" : "opacity-40"}`} data-testid={`gallery-item-${item.id}`}>
                <img src={fileSrc(item.file_id)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#141C16] via-transparent to-transparent flex flex-col justify-end p-3 opacity-0 hover:opacity-100 transition">
                  <div className="flex flex-wrap gap-1 justify-end">
                    <IconBtn onClick={() => move(i, -1)} disabled={i === 0} testId={`move-up-${item.id}`}><MoveUp className="w-3 h-3" /></IconBtn>
                    <IconBtn onClick={() => move(i, 1)} disabled={i === gallery.length - 1} testId={`move-down-${item.id}`}><MoveDown className="w-3 h-3" /></IconBtn>
                    <IconBtn onClick={() => toggleVisible(item)} testId={`toggle-${item.id}`}>
                      {item.is_visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    </IconBtn>
                    <IconBtn onClick={() => remove(item)} danger testId={`delete-${item.id}`}><Trash2 className="w-3 h-3" /></IconBtn>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, disabled, danger, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`p-1.5 bg-[#141C16]/80 border ${danger ? "border-[#CF6679]/40 hover:border-[#CF6679] text-[#CF6679]" : "border-[#D2A853]/40 hover:border-[#D2A853] text-[#D2A853]"} disabled:opacity-30`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Reminders Due Panel — Tap-to-Send + Web Push enable
// ---------------------------------------------------------------------------
function RemindersDuePanel() {
  const [items, setItems] = useState(null);
  const [pushReady, setPushReady] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/reminders/due");
      setItems(data.due || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setItems([]);
    }
  }, []);

  const refreshSubState = useCallback(async () => {
    if (!pushSupported()) { setPushReady(false); return; }
    const sub = await getSubscription();
    setPushReady(!!sub);
  }, []);

  useEffect(() => { load(); refreshSubState(); }, [load, refreshSubState]);

  const turnOn = async () => {
    setPushBusy(true);
    try {
      await enableAdminPush();
      toast.success("Push notifications enabled.");
      await refreshSubState();
    } catch (e) {
      toast.error(e.message || "Could not enable push.");
    } finally { setPushBusy(false); }
  };
  const turnOff = async () => {
    setPushBusy(true);
    try {
      await disableAdminPush();
      toast.success("Push notifications disabled.");
      await refreshSubState();
    } catch (e) {
      toast.error(e.message || "Could not disable push.");
    } finally { setPushBusy(false); }
  };
  const test = async () => {
    setPushBusy(true);
    try {
      const r = await sendAdminPushTest();
      if (r.delivered > 0) {
        toast.success(`Test push sent — ${r.delivered}/${r.subscriptions} delivered.`);
      } else if (r.subscriptions === 0) {
        toast.error("No push subscription on file yet — tap Enable Push first.");
      } else {
        const first = (r.errors && r.errors[0]) || {};
        const code = first.status_code ? ` (${first.status_code})` : "";
        const reason = first.reason || "unknown";
        toast.error(`Delivery failed${code}: ${reason}`, { duration: 10000 });
        // eslint-disable-next-line no-console
        console.error("[PAID DEWZ push test] full response:", r);
      }
    } catch (e) {
      toast.error(e.message || "Test push failed.");
    } finally { setPushBusy(false); }
  };

  return (
    <div data-testid="admin-reminders-panel" className="space-y-8">
      {/* Push enablement */}
      <div className="frame-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-xl">
            <span className="overline text-[#D2A853]">Admin Push Notifications</span>
            <h3 className="font-display text-xl text-[#E8E9E3] mt-2">Get reminders on your phone</h3>
            <p className="text-sm text-[#A3A8A4] mt-2 leading-relaxed">
              Receive a push 72 hours before tattoo appointments (deposit-policy critical),
              24 hours before all appointments, and 1 hour before. Tap a notification to jump
              straight to the appointment.
            </p>
            {!pushSupported() && (
              <p className="text-xs text-[#CF6679] mt-3" data-testid="push-unsupported">
                This browser doesn&rsquo;t support web push. {isIOS() && !isStandalone() ? "On iPhone: tap Share → Add to Home Screen, then open the installed app." : ""}
              </p>
            )}
            {pushSupported() && isIOS() && !isStandalone() && (
              <p className="text-xs text-[#D2A853] mt-3" data-testid="push-ios-hint">
                On iPhone, install this site to your Home Screen first (Share → Add to Home Screen), then open the installed app to enable push.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {pushReady ? (
              <>
                <button onClick={test} disabled={pushBusy} className="btn-ghost-sm inline-flex items-center gap-2" data-testid="push-test">
                  {pushBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
                  Send Test
                </button>
                <button onClick={turnOff} disabled={pushBusy} className="btn-ghost-sm inline-flex items-center gap-2" data-testid="push-disable">
                  <BellOff className="w-3.5 h-3.5" /> Turn Off
                </button>
              </>
            ) : (
              <button onClick={turnOn} disabled={pushBusy || !pushSupported()} className="btn-brass-sm inline-flex items-center gap-2" data-testid="push-enable">
                {pushBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
                Enable Push
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Reminders Due list */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <span className="overline text-[#D2A853]">Reminders Due</span>
          <button onClick={load} className="overline text-[#A3A8A4] hover:text-[#D2A853]" data-testid="reminders-refresh">Refresh</button>
        </div>
        {items === null ? (
          <div className="frame-card p-6 flex items-center gap-2 text-sm text-[#A3A8A4]">
            <Loader2 className="w-4 h-4 animate-spin text-[#D2A853]" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="frame-card p-8 text-center text-sm text-[#A3A8A4]" data-testid="reminders-empty">
            Nothing due in the next 96 hours. You&rsquo;ll see Tap-to-Send buttons here as appointments approach.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <ReminderRow
                key={`${it.appointment_id}-${it.bucket}`}
                item={it}
                isOpen={openId === `${it.appointment_id}-${it.bucket}`}
                onToggle={() => setOpenId(openId === `${it.appointment_id}-${it.bucket}` ? null : `${it.appointment_id}-${it.bucket}`)}
                onChanged={load}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReminderRow({ item, isOpen, onToggle, onChanged }) {
  const start = new Date(item.start);
  const bucketLabel = item.bucket === "72h" ? "72-hour" : item.bucket === "24h" ? "24-hour" : "1-hour";
  return (
    <div className="frame-card p-5" data-testid={`reminder-row-${item.appointment_id}-${item.bucket}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-[#D2A853] border border-[#D2A853]/40 px-2 py-0.5">
              {bucketLabel}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-[#A3A8A4]">
              {item.appointment_type}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-[#A3A8A4]">
              · in {item.hours_until} h
            </span>
          </div>
          <div className="font-display text-lg text-[#E8E9E3] mt-2 truncate">{item.client_name || "(no name)"}</div>
          <div className="text-xs text-[#A3A8A4] mt-1">
            {start.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            {item.client_phone && <span> · {item.client_phone}</span>}
          </div>
        </div>
        <button
          onClick={onToggle}
          className="btn-brass-sm inline-flex items-center gap-2 shrink-0"
          data-testid={`reminder-open-${item.appointment_id}-${item.bucket}`}
        >
          <MessageSquare className="w-3.5 h-3.5" /> Tap-to-Send
        </button>
      </div>
      {isOpen && (
        <div className="mt-5 pt-5 border-t border-[#D2A853]/15">
          <SmsTapToSend appointmentId={item.appointment_id} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Danger Zone — single-client permanent delete
// ---------------------------------------------------------------------------
function DangerZone({ clientId, user, onClose, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const expected = (user.email || user.name || "DELETE").trim();

  const doDelete = async () => {
    if (confirmText.trim().toLowerCase() !== expected.toLowerCase()) {
      toast.error(`Type "${expected}" to confirm.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await http.delete(`/admin/clients/${clientId}`);
      toast.success(`Deleted ${data.deleted_email}`);
      onUpdate?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="danger-zone" className="mt-10 pt-6 border-t border-[#CF6679]/30">
      <div className="flex items-center justify-between mb-3">
        <span className="overline text-[#CF6679]">Danger Zone</span>
      </div>
      {!showConfirm ? (
        <div className="frame-card p-5 border-[#CF6679]/40">
          <p className="text-sm text-[#A3A8A4] leading-relaxed mb-4">
            Permanently delete this client and every record linked to them —
            intake, references, notes, messages, payments, appointments, and
            audit log. Any held slots are released. This cannot be undone.
          </p>
          <button
            onClick={() => setShowConfirm(true)}
            className="btn-ghost-sm inline-flex items-center gap-2 !text-[#CF6679] !border-[#CF6679]/40 hover:!border-[#CF6679]"
            data-testid="client-delete-request"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete Client
          </button>
        </div>
      ) : (
        <div className="frame-card p-5 border-[#CF6679]/60" data-testid="client-delete-confirm">
          <p className="text-sm text-[#E8E9E3] leading-relaxed mb-4">
            Type <span className="text-[#CF6679] font-mono">{expected}</span> below to confirm permanent deletion.
          </p>
          <input
            type="text"
            className="input-line mb-4"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={expected}
            data-testid="client-delete-confirm-input"
          />
          <div className="flex flex-wrap gap-2">
            <button
              onClick={doDelete}
              disabled={busy || confirmText.trim().toLowerCase() !== expected.toLowerCase()}
              className="btn-brass-sm inline-flex items-center gap-2 !bg-[#CF6679] hover:!bg-[#d97c8c] !text-[#141C16]"
              data-testid="client-delete-confirm-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Permanently Delete
            </button>
            <button
              onClick={() => { setShowConfirm(false); setConfirmText(""); }}
              disabled={busy}
              className="btn-ghost-sm"
              data-testid="client-delete-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data Cleanup Panel — bulk-purge pytest artifacts + clear notifications
// Rendered at the bottom of the Studio Settings tab.
// ---------------------------------------------------------------------------
function DataCleanupPanel() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [clearingNotifs, setClearingNotifs] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/test-data/preview");
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const purge = async () => {
    if (!preview || preview.count === 0) {
      toast.error("Nothing to purge.");
      return;
    }
    if (!window.confirm(
      `Permanently delete ${preview.count} test client${preview.count === 1 ? "" : "s"} ` +
      `and ${preview.consultation_leads} test consultation lead${preview.consultation_leads === 1 ? "" : "s"}? ` +
      `This cannot be undone.`
    )) return;
    setPurging(true);
    try {
      const { data } = await http.post("/admin/test-data/purge");
      toast.success(
        `Purged ${data.purged_clients} clients, ${data.purged_leads} leads, ${data.purged_orphan_notifications} orphan notifications.`
      );
      setPreview(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setPurging(false); }
  };

  const clearNotifs = async () => {
    if (!window.confirm("Clear ALL admin notifications? This cannot be undone.")) return;
    setClearingNotifs(true);
    try {
      const { data } = await http.post("/admin/notifications/clear-all");
      toast.success(`Cleared ${data.deleted} notifications.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setClearingNotifs(false); }
  };

  return (
    <div className="frame-card p-6 border-[#CF6679]/30" data-testid="data-cleanup-panel">
      <span className="overline text-[#CF6679]">Data Cleanup</span>
      <h3 className="font-display text-xl text-[#E8E9E3] mt-2">Purge Test Data</h3>
      <p className="text-sm text-[#A3A8A4] mt-2 leading-relaxed">
        Removes every client created by the automated testing suite — anyone
        whose email ends in <span className="font-mono text-[#E2BC6A]">@example.com</span>{" "}
        or whose name starts with <span className="font-mono text-[#E2BC6A]">TEST_iter</span>,{" "}
        plus their intake, references, payments, appointments, notes, messages,
        and audit log. Safe to run on real production data — real clients are
        never matched.
      </p>

      <div className="flex flex-wrap gap-2 mt-5">
        <button
          onClick={loadPreview}
          disabled={loading}
          className="btn-ghost-sm inline-flex items-center gap-2"
          data-testid="cleanup-preview"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
          Preview matches
        </button>
        {preview && preview.count > 0 && (
          <button
            onClick={purge}
            disabled={purging}
            className="btn-brass-sm inline-flex items-center gap-2 !bg-[#CF6679] hover:!bg-[#d97c8c]"
            data-testid="cleanup-purge"
          >
            {purging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Purge {preview.count} test clients
          </button>
        )}
      </div>

      {preview && (
        <div className="mt-6 pt-5 border-t border-[#D2A853]/15" data-testid="cleanup-preview-result">
          {preview.count === 0 ? (
            <p className="text-sm text-[#4CAF50]">No test clients found — your database is clean.</p>
          ) : (
            <>
              <p className="text-sm text-[#E8E9E3] mb-3">
                Matched <span className="text-[#D2A853]">{preview.count} client{preview.count === 1 ? "" : "s"}</span>{" "}
                and <span className="text-[#D2A853]">{preview.consultation_leads} consultation lead{preview.consultation_leads === 1 ? "" : "s"}</span>.
              </p>
              <div className="max-h-64 overflow-y-auto border border-[#D2A853]/15 p-3 space-y-1">
                {preview.clients.slice(0, 200).map((c) => (
                  <div key={c.id} className="text-xs text-[#A3A8A4]">
                    <span className="text-[#E8E9E3]">{c.name || "(no name)"}</span>
                    <span className="mx-2">·</span>
                    {c.email}
                    {c.phone && <span className="mx-2">·</span>}
                    {c.phone}
                  </div>
                ))}
                {preview.clients.length > 200 && (
                  <div className="text-xs text-[#A3A8A4] italic mt-2">
                    + {preview.count - 200} more not shown.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <div className="mt-8 pt-6 border-t border-[#CF6679]/30">
        <h4 className="overline text-[#CF6679] mb-2">Clear all notifications</h4>
        <p className="text-xs text-[#A3A8A4] mb-3 leading-relaxed">
          Wipes the admin bell feed. Useful after importing or purging test data.
        </p>
        <button
          onClick={clearNotifs}
          disabled={clearingNotifs}
          className="btn-ghost-sm inline-flex items-center gap-2 !text-[#CF6679] !border-[#CF6679]/40"
          data-testid="cleanup-clear-notifs"
        >
          {clearingNotifs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          Clear all notifications
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Needs Attention — top-of-dashboard priority feed.
// Polls /admin/needs-attention every 30s. Each card is one tap to open the
// matching client drawer or jump to the Reminders tab.
// ---------------------------------------------------------------------------
function NeedsAttentionPanel({ onOpenClient, onOpenAppt }) {
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/needs-attention");
      setItems(data.items || []);
    } catch (_e) {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (items === null) {
    return (
      <div className="frame-card p-5 mb-6 flex items-center gap-2 text-sm text-[#A3A8A4]" data-testid="needs-attention-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#D2A853]" /> Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="frame-card p-5 mb-6 text-center text-sm text-[#A3A8A4]" data-testid="needs-attention-empty">
        <CheckCircle2 className="w-5 h-5 mx-auto mb-2 text-[#4CAF50]" strokeWidth={1.5} />
        Nothing needs your attention right now.
      </div>
    );
  }
  return (
    <section className="mb-6 md:mb-8" data-testid="needs-attention">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-[#CF6679]" strokeWidth={1.6} />
        <span className="overline text-[#CF6679]">Needs Attention</span>
        <span className="text-xs text-[#A3A8A4] ml-auto">{items.length} item{items.length === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-2.5">
        {items.map((it, idx) => (
          <NeedsAttentionCard
            key={`${it.kind}-${it.appointment_id || it.client_id || it.lead_id || idx}`}
            item={it}
            onOpenClient={onOpenClient}
            onOpenAppt={onOpenAppt}
          />
        ))}
      </div>
    </section>
  );
}

function NeedsAttentionCard({ item, onOpenClient, onOpenAppt }) {
  const isRed = item.color === "red";
  const borderClass = isRed ? "border-[#CF6679]/50 hover:border-[#CF6679]" : "border-[#D2A853]/50 hover:border-[#D2A853]";
  const accentClass = isRed ? "text-[#CF6679]" : "text-[#D2A853]";
  const handleTap = () => {
    if (item.appointment_id) {
      onOpenAppt?.(item.appointment_id);
    } else if (item.client_id) {
      onOpenClient?.(item.client_id);
    }
  };
  return (
    <button
      onClick={handleTap}
      className={`frame-card w-full text-left p-4 active:bg-[#D2A853]/5 transition-colors ${borderClass}`}
      data-testid={`needs-attention-card-${item.kind}`}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${accentClass}`}>
          {isRed ? <AlertTriangle className="w-5 h-5" strokeWidth={1.8} /> : <BellRing className="w-5 h-5" strokeWidth={1.8} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base md:text-lg text-[#E8E9E3] leading-tight">
            {item.headline}
          </div>
          <div className={`text-xs md:text-sm mt-1 ${accentClass}`}>
            {item.subline}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-[#A3A8A4] shrink-0 self-center" strokeWidth={1.4} />
      </div>
    </button>
  );
}

